import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Index from './pages/Index';
import PricingPage from './pages/PricingPage';
import DokumentaciaPage from './pages/DokumentaciaPage';
import AITestBot from './pages/AITestBot';
import TaxiDispatcher from './pages/TaxiDispatcher';
import AuthPage from './pages/AuthPage';
import AdminDashboard from './pages/AdminDashboard';
import DriverDashboard from './pages/DriverDashboard';
import './App.css';

// Scroll reveal utility function
export const initScrollReveal = () => {
  const observerOptions = {
    threshold: 0.1,
    rootMargin: '0px 0px -20px 0px'
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('revealed');
      }
    });
  }, observerOptions);

  // Observe all elements with scroll-reveal classes
  const scrollElements = document.querySelectorAll('.scroll-reveal, .scroll-reveal-left, .scroll-reveal-right');
  
  scrollElements.forEach((el, index) => {
    // Remove existing revealed class to allow re-animation
    el.classList.remove('revealed');
    
    // If element is already in viewport (like hero), reveal it immediately
    const rect = el.getBoundingClientRect();
    const isInViewport = rect.top >= 0 && rect.top <= window.innerHeight;
    
    if (isInViewport) {
      // Delay based on the delay class
      const delayClass = Array.from(el.classList).find(cls => cls.includes('delay'));
      const delay = delayClass ? parseInt(delayClass.split('-')[3]) * 100 : 0;
      
      setTimeout(() => {
        el.classList.add('revealed');
      }, delay);
    }
    
    observer.observe(el);
  });

  // Cleanup function
  return () => {
    scrollElements.forEach(el => observer.unobserve(el));
  };
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole = null }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  // Check role if specified
  if (requiredRole && user?.role !== requiredRole) {
    // Redirect to appropriate dashboard based on user role
    const redirectPath = user?.role === 'ADMIN' ? '/admin' : '/driver';
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    // Redirect to appropriate dashboard
    const redirectPath = user?.role === 'ADMIN' ? '/admin' : '/driver';
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

// Component to handle route changes
const AppContent = () => {
  const location = useLocation();

  useEffect(() => {
    // Reinitialize scroll reveal on route change
    const timer = setTimeout(() => {
      initScrollReveal();
    }, 100);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Index />} />
      <Route path="/cennik" element={<PricingPage />} />
      <Route path="/dokumentacia" element={<DokumentaciaPage />} />
      <Route path="/testingai" element={<AITestBot />} />
      <Route path="/taxi-dispatcher" element={<TaxiDispatcher />} />
      
      {/* Auth route (redirect if already authenticated) */}
      <Route 
        path="/auth" 
        element={
          <PublicRoute>
            <AuthPage />
          </PublicRoute>
        } 
      />
      
      {/* Protected routes */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute requiredRole="ADMIN">
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/driver" 
        element={
          <ProtectedRoute requiredRole="DRIVER">
            <DriverDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Generic dashboard redirect */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Navigate to="/admin" replace />
          </ProtectedRoute>
        } 
      />
      
      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setIsLoading(false);
      
      // Initial scroll reveal setup
      setTimeout(() => {
        initScrollReveal();
      }, 100);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <AppContent />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
